import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { X, ShieldCheck, Loader2, ArrowLeft, CreditCard } from "lucide-react";
import PlanChooser from "@/components/checkout/PlanChooser";
import PaymentSuccess from "@/components/checkout/PaymentSuccess";
import { money } from "@/lib/format";

export { money };

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sdkPromises = {};
export const loadPayPalSdk = (clientId, currency, subscription = false) => {
  const ns = subscription ? "paypal_subs" : "paypal";
  if (window[ns]) return Promise.resolve(window[ns]);
  if (sdkPromises[ns]) return sdkPromises[ns];
  sdkPromises[ns] = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    const intent = subscription ? "&vault=true&intent=subscription" : "&intent=capture";
    script.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(
      clientId
    )}&currency=${currency}${intent}&components=buttons`;
    if (subscription) script.setAttribute("data-namespace", ns);
    script.onload = () => resolve(window[ns]);
    script.onerror = () => {
      sdkPromises[ns] = null;
      reject(new Error("PayPal SDK failed to load"));
    };
    document.body.appendChild(script);
  });
  return sdkPromises[ns];
};

export function usePaymentsConfig() {
  const [config, setConfig] = useState(null);
  useEffect(() => {
    axios
      .get(`${API}/payments/config`)
      .then((res) => setConfig(res.data))
      .catch(() => setConfig({ configured: false }));
  }, []);
  return config;
}

const BUTTON_STYLE = { layout: "vertical", color: "black", shape: "rect", label: "paypal", height: 48 };

const buildSubscriptionButtons = (paypal, { packageId, cycles, payer, refCode, setState, setReceipt }) =>
  paypal.Buttons({
    style: BUTTON_STYLE,
    createSubscription: async (data, actions) => {
      const { data: res } = await axios.post(`${API}/payments/paypal/subscription-plan`, {
        package_id: packageId,
        cycles,
      });
      return actions.subscription.create({ plan_id: res.plan_id, custom_id: packageId });
    },
    onApprove: async (data) => {
      setState("processing");
      try {
        const res = await axios.post(`${API}/payments/paypal/subscription-activated`, {
          subscription_id: data.subscriptionID,
          package_id: packageId,
          cycles,
          name: payer?.name || null,
          email: payer?.email || null,
          ref_code: refCode || null,
        });
        setReceipt(res.data);
        setState("subscribed");
        toast.success("Payment plan activated — confirmation on its way.");
      } catch {
        setState("ready");
        toast.error("PayPal is still confirming your plan. We will email you once it is active.");
      }
    },
    onCancel: () => toast.info("Plan setup cancelled — nothing was charged."),
    onError: (err) => {
      console.error(err);
      setState("ready");
      toast.error("PayPal could not set up the plan. Please try again.");
    },
  });

const buildOrderButtons = (paypal, { packageId, payer, bookingId, cycles, refCode, setState, setReceipt }) =>
  paypal.Buttons({
    style: BUTTON_STYLE,
    createOrder: async () => {
      const { data } = await axios.post(`${API}/payments/paypal/order`, {
        package_id: packageId,
        name: payer?.name || null,
        email: payer?.email || null,
        booking_id: bookingId || null,
        cycles: cycles || null,
        ref_code: refCode || null,
      });
      return data.order_id;
    },
    onApprove: async (data) => {
      setState("processing");
      const res = await axios.post(`${API}/payments/paypal/capture`, { order_id: data.orderID });
      if (res.data.status === "paid") {
        setReceipt(res.data);
        setState("paid");
        toast.success("Payment received — receipt on its way.");
      } else {
        setState("ready");
        toast.error("PayPal is still processing this payment. We will email you once it clears.");
      }
    },
    onCancel: () => toast.info("Payment cancelled — nothing was charged."),
    onError: (err) => {
      console.error(err);
      setState("ready");
      toast.error("PayPal could not complete the payment. Please try again.");
    },
  });

export default function PayPalCheckout({ packageId, label, price, note, payer, bookingId, config, onClose }) {
  const containerRef = useRef(null);
  const instalments = config?.instalments?.[packageId] || [];
  const [step, setStep] = useState(instalments.length ? "choose" : "pay");
  const [plan, setPlan] = useState(null);
  const [method, setMethod] = useState("auto");
  const [state, setState] = useState("loading");
  const [receipt, setReceipt] = useState(null);
  const [refValid, setRefValid] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const refCode = localStorage.getItem("bc_ref_code");

  const payWithStripe = async () => {
    setStripeLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/stripe/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin,
        name: payer?.name || null,
        email: payer?.email || null,
        booking_id: bookingId || null,
        ref_code: refCode || null,
      });
      window.location.href = data.checkout_url;
    } catch {
      setStripeLoading(false);
      toast.error("Card checkout is unavailable right now. Please try PayPal or email us.");
    }
  };

  useEffect(() => {
    if (!refCode) return;
    axios
      .get(`${API}/referrals/validate/${refCode}`)
      .then((res) => setRefValid(!!res.data.valid))
      .catch(() => {});
  }, [refCode]);

  useEffect(() => {
    if (!config?.configured || step !== "pay") return;
    let cancelled = false;
    setState("loading");
    const isAuto = plan && method === "auto";
    loadPayPalSdk(config.client_id, config.currency || "USD", isAuto)
      .then((paypal) => {
        if (cancelled || !containerRef.current) return;
        containerRef.current.replaceChildren();
        setState("ready");
        const opts = { packageId, payer, bookingId, cycles: plan?.cycles, refCode, setState, setReceipt };
        const buttons = isAuto ? buildSubscriptionButtons(paypal, opts) : buildOrderButtons(paypal, opts);
        buttons.render(containerRef.current).catch(() => setState("error"));
      })
      .catch(() => setState("error"));
    return () => {
      cancelled = true;
    };
  }, [config, step, plan, method, packageId, payer, bookingId, refCode]);

  const discountEligible = refValid && !plan && packageId !== "consult_deposit";
  const discountedPrice = discountEligible
    ? money(Number(String(price).replace(/[^0-9.]/g, "")) * 0.9)
    : null;
  const payAmount = plan ? `${money(plan.amount)}/mo` : price;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-pine/80 px-5 backdrop-blur-md"
        data-testid="paypal-modal"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.45, ease: [0.76, 0, 0.24, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="relative max-h-[90vh] w-full max-w-md overflow-y-auto border border-linen bg-bone p-8 md:p-10"
        >
          <button
            onClick={onClose}
            aria-label="Close payment"
            data-testid="paypal-modal-close"
            className="absolute right-5 top-5 text-pine/40 transition-colors hover:text-terra"
          >
            <X size={18} />
          </button>

          {state === "paid" || state === "subscribed" ? (
            <PaymentSuccess state={state} receipt={receipt} plan={plan} onClose={onClose} />
          ) : step === "choose" ? (
            <PlanChooser
              label={label}
              price={price}
              instalments={instalments}
              plan={plan}
              setPlan={setPlan}
              method={method}
              setMethod={setMethod}
              showRefDiscount={refValid && packageId !== "consult_deposit"}
              onContinue={() => setStep("pay")}
            />
          ) : (
            <>
              {instalments.length > 0 && (
                <button
                  type="button"
                  data-testid="paypal-back"
                  onClick={() => setStep("choose")}
                  className="mb-4 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-pine/50 transition-colors hover:text-terra"
                >
                  <ArrowLeft size={12} /> Change plan
                </button>
              )}
              <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-terra">Secure checkout</p>
              <h3 className="mb-2 font-serif text-2xl font-medium leading-tight text-pine" data-testid="paypal-modal-label">
                {label}
              </h3>
              <p className="mb-1 font-serif text-4xl font-semibold tracking-tight text-forest" data-testid="paypal-modal-amount">
                {discountedPrice || payAmount}
              </p>
              {discountedPrice && (
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-terra" data-testid="paypal-referral-discount">
                  10% referral discount applied — was {price}
                </p>
              )}
              {plan && (
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-pine/50" data-testid="paypal-plan-summary">
                  {plan.cycles} payments · {method === "auto" ? "charged automatically" : "first instalment today, link emailed monthly"}
                </p>
              )}
              {note && !plan && <p className="mb-8 mt-3 text-sm leading-relaxed text-pine/60">{note}</p>}
              {plan && <div className="mb-6" />}

              <div className="min-h-[112px]" ref={containerRef} data-testid="paypal-buttons" />

              {config?.stripe_configured && !plan && state !== "processing" && (
                <>
                  <div className="my-4 flex items-center gap-3">
                    <span className="h-px flex-1 bg-linen" />
                    <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-pine/40">or</span>
                    <span className="h-px flex-1 bg-linen" />
                  </div>
                  <button
                    type="button"
                    onClick={payWithStripe}
                    disabled={stripeLoading}
                    data-testid="stripe-checkout-btn"
                    className="flex w-full items-center justify-center gap-2 border border-pine bg-pine px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-transparent hover:text-pine disabled:opacity-60"
                  >
                    {stripeLoading ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
                    Pay by card — Stripe checkout
                  </button>
                </>
              )}

              {state === "loading" && (
                <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-pine/40">
                  <Loader2 size={13} className="animate-spin" /> Loading PayPal…
                </p>
              )}
              {state === "processing" && (
                <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-forest" data-testid="paypal-processing">
                  <Loader2 size={13} className="animate-spin" /> Confirming your payment…
                </p>
              )}
              {state === "error" && (
                <p className="text-sm text-terra" data-testid="paypal-error">
                  PayPal is unavailable right now. Please email Info@bookcarellc.com and we will send you a payment link.
                </p>
              )}

              <p className="mt-7 flex items-start gap-2 border-t border-linen pt-5 font-mono text-[9px] uppercase leading-relaxed tracking-[0.15em] text-pine/40">
                <ShieldCheck size={13} className="mt-px shrink-0 text-forest" />
                Payments are processed securely by PayPal or Stripe — we never see your card details.
              </p>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
